'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { PLANS } from '@/lib/billing/plans'
import { usePathname } from 'next/navigation'
import {
  LayoutDashboard,
  Wallet,
  CalendarDays,
  Trophy,
  Settings,
  Menu,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  Calculator,
  Briefcase,
  FileText,
  BookOpen,
  Newspaper,
  Sparkles,
  ChevronDown,
  FileCheck2,
  HandCoins,
  LineChart,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/lib/auth/context'
import {
  FINANCEIRO_NAV,
  CALCULADORA_LINKS,
  HISTORICO_CALCULOS,
  FERRAMENTAS_NAV,
  RECURSOS_NAV,
  isCalculadorasSection,
  isSidebarLinkActive,
} from '@/lib/navigation/sidebar-nav'

const FINANCEIRO_ICONS = {
  '/dashboard': LayoutDashboard,
  '/dashboard/planejamento': Wallet,
  '/registrar': CalendarDays,
} as const

const RECURSOS_ICONS = {
  '/atividades-mei': Briefcase,
  '/templates': FileText,
  '/glossario': BookOpen,
  '/blog': Newspaper,
} as const

const FERRAMENTAS_ICONS = {
  '/comprovante-de-faturamento-mei': FileCheck2,
  '/quanto-posso-me-pagar-mei': HandCoins,
  '/dre-simplificado-mei': LineChart,
} as const

const settingsItem = {
  href: '/dashboard/configuracoes',
  label: 'Configurações',
  icon: Settings,
}

const mobileShortcuts = [
  { href: '/dashboard', label: 'Painel', icon: LayoutDashboard },
  { href: '/dashboard/planejamento', label: 'Orçamento', icon: Wallet },
  { href: '/calculadoras', label: 'Calculadoras', icon: Calculator },
] as const

function NavLink({
  href,
  label,
  Icon,
  active,
  collapsed,
  onNavigate,
  sub = false,
}: {
  href: string
  label: string
  Icon?: typeof LayoutDashboard
  active: boolean
  collapsed: boolean
  onNavigate?: () => void
  sub?: boolean
}) {
  if (sub) {
    return (
      <Link
        href={href}
        onClick={onNavigate}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'block rounded-md py-1.5 pl-9 pr-3 text-[12px] font-semibold transition-colors',
          active
            ? 'bg-secondary text-foreground'
            : 'text-foreground/65 hover:bg-secondary/60 hover:text-foreground'
        )}
      >
        {label}
      </Link>
    )
  }

  return (
    <Link
      href={href}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      title={collapsed ? label : undefined}
      className={cn(
        'flex items-center gap-3 rounded-md font-semibold text-[13px] transition-colors',
        collapsed ? 'justify-center px-0 h-10' : 'px-3 h-9',
        active
          ? collapsed
            ? 'bg-secondary text-foreground'
            : 'bg-secondary text-foreground border-l-2 border-[rgb(var(--green-deep))]'
          : 'text-foreground/70 hover:bg-secondary/60 hover:text-foreground'
      )}
    >
      {Icon && <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />}
      {!collapsed && <span className="truncate">{label}</span>}
    </Link>
  )
}

function CalculadorasGroup({
  pathname,
  collapsed,
  onNavigate,
}: {
  pathname: string
  collapsed: boolean
  onNavigate?: () => void
}) {
  const sectionActive = isCalculadorasSection(pathname)
  const [open, setOpen] = useState(sectionActive)

  useEffect(() => {
    if (sectionActive) setOpen(true)
  }, [sectionActive])

  if (collapsed) {
    return (
      <Link
        href="/calculadoras"
        title="Calculadoras"
        className={cn(
          'flex items-center justify-center h-10 rounded-md transition-colors',
          sectionActive
            ? 'bg-secondary text-foreground'
            : 'text-foreground/70 hover:bg-secondary/60 hover:text-foreground'
        )}
      >
        <Calculator className="w-4 h-4" aria-hidden="true" />
      </Link>
    )
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          'flex w-full items-center gap-3 rounded-md px-3 h-9 font-semibold text-[13px] transition-colors',
          sectionActive
            ? 'bg-secondary text-foreground border-l-2 border-[rgb(var(--green-deep))]'
            : 'text-foreground/70 hover:bg-secondary/60 hover:text-foreground'
        )}
      >
        <Calculator className="w-4 h-4 shrink-0" aria-hidden="true" />
        <span className="flex-1 truncate text-left">Calculadoras</span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-foreground/45 transition-transform', open && 'rotate-180')}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="mt-0.5 space-y-0.5">
          <NavLink
            href="/calculadoras"
            label="Ver todas"
            active={isSidebarLinkActive(pathname, '/calculadoras')}
            collapsed={false}
            onNavigate={onNavigate}
            sub
          />
          {CALCULADORA_LINKS.map((item) => (
            <NavLink
              key={item.href}
              href={item.href}
              label={item.label}
              active={isSidebarLinkActive(pathname, item.href)}
              collapsed={false}
              onNavigate={onNavigate}
              sub
            />
          ))}
          <div className="mx-9 my-1 border-t border-border" aria-hidden="true" />
          <NavLink
            href={HISTORICO_CALCULOS.href}
            label={HISTORICO_CALCULOS.label}
            active={isSidebarLinkActive(pathname, HISTORICO_CALCULOS.href)}
            collapsed={false}
            onNavigate={onNavigate}
            sub
          />
        </div>
      )}
    </div>
  )
}

function SectionLabel({ children, collapsed }: { children: React.ReactNode; collapsed?: boolean }) {
  if (collapsed) {
    return <div className="mx-2 my-2 border-t border-border" aria-hidden="true" />
  }
  return (
    <p className="px-3 pb-1 pt-3 text-[10.5px] font-bold uppercase tracking-[0.09em] text-foreground/40">
      {children}
    </p>
  )
}

function SidebarNav({
  pathname,
  collapsed = false,
  onNavigate,
  user,
}: {
  pathname: string
  collapsed?: boolean
  onNavigate?: () => void
  user?: { name: string | null; plano: string; role: string } | null
}) {
  const isPremium = user?.plano === 'PREMIUM' || user?.role === 'ADMIN'

  return (
    <nav className="flex flex-col h-full px-2 py-3" aria-label="Navegação da área logada">
      <div className="flex-1 flex flex-col gap-1">
        {isPremium && (
          <>
            <SectionLabel collapsed={collapsed}>Financeiro</SectionLabel>
            <ul className="flex flex-col gap-0.5">
              {FINANCEIRO_NAV.map((item) => {
                const Icon = FINANCEIRO_ICONS[item.href as keyof typeof FINANCEIRO_ICONS]
                return (
                  <li key={item.href}>
                    <NavLink
                      href={item.href}
                      label={item.label}
                      Icon={Icon}
                      active={isSidebarLinkActive(pathname, item.href)}
                      collapsed={collapsed}
                      onNavigate={onNavigate}
                    />
                  </li>
                )
              })}
            </ul>
            <div className="my-2 border-t border-border" aria-hidden="true" />
          </>
        )}

        <div className="space-y-0.5">
          <CalculadorasGroup pathname={pathname} collapsed={collapsed} onNavigate={onNavigate} />
        </div>

        {isPremium && (
          <>
            <SectionLabel collapsed={collapsed}>Ferramentas</SectionLabel>
            <ul className="flex flex-col gap-0.5">
              {FERRAMENTAS_NAV.map((item) => {
                const Icon = FERRAMENTAS_ICONS[item.href as keyof typeof FERRAMENTAS_ICONS]
                return (
                  <li key={item.href}>
                    <NavLink
                      href={item.href}
                      label={item.label}
                      Icon={Icon}
                      active={isSidebarLinkActive(pathname, item.href)}
                      collapsed={collapsed}
                      onNavigate={onNavigate}
                    />
                  </li>
                )
              })}
            </ul>
          </>
        )}

        <SectionLabel collapsed={collapsed}>Conteúdo</SectionLabel>

        <ul className="flex flex-col gap-0.5">
          {RECURSOS_NAV.map((item) => {
            const Icon = RECURSOS_ICONS[item.href as keyof typeof RECURSOS_ICONS]
            return (
              <li key={item.href}>
                <NavLink
                  href={item.href}
                  label={item.label}
                  Icon={Icon}
                  active={isSidebarLinkActive(pathname, item.href)}
                  collapsed={collapsed}
                  onNavigate={onNavigate}
                />
              </li>
            )
          })}
        </ul>
      </div>

      <div className="mt-2 border-t border-border pt-2 space-y-0.5">
        {isPremium && (
          <NavLink
            href="/dashboard/conquistas"
            label="Conquistas"
            Icon={Trophy}
            active={isSidebarLinkActive(pathname, '/dashboard/conquistas')}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
        )}

        {!isPremium && (
          <Link
            href="/premium"
            onClick={onNavigate}
            title={collapsed ? 'Premium' : undefined}
            className={cn(
              'flex items-center gap-3 rounded-md font-semibold text-[13px] text-[rgb(var(--green-deep))] transition-colors hover:bg-secondary/60',
              collapsed ? 'justify-center px-0 h-10' : 'px-3 h-9'
            )}
          >
            <Sparkles className="w-4 h-4 shrink-0" aria-hidden="true" />
            {!collapsed && <span className="truncate">Premium · {PLANS.PREMIUM.priceDisplay}/mês</span>}
          </Link>
        )}

        <div className={cn('flex items-center gap-2 pt-1', collapsed ? 'justify-center' : 'px-1')}>
          <Link
            href={settingsItem.href}
            onClick={onNavigate}
            title="Conta e configurações"
            className={cn(
              'flex min-w-0 flex-1 items-center gap-2.5 rounded-md py-1.5 hover:bg-secondary/60',
              collapsed ? 'justify-center px-0' : 'px-2'
            )}
          >
            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[rgb(var(--green-soft))] text-[13px] font-bold text-[rgb(var(--green-deep))]">
              {(user?.name?.[0] ?? 'C').toUpperCase()}
            </span>
            {!collapsed && (
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-bold text-foreground">
                  {user?.name?.split(' ')[0] ?? 'Minha conta'}
                </span>
                <span className="block text-[12px] text-foreground/55">
                  {isPremium ? 'Premium' : 'Plano grátis'}
                </span>
              </span>
            )}
          </Link>
          {!collapsed && (
            <Link
              href={settingsItem.href}
              onClick={onNavigate}
              aria-label="Configurações"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-foreground/50 hover:bg-secondary hover:text-foreground"
            >
              <Settings className="h-4 w-4" />
            </Link>
          )}
        </div>
      </div>
    </nav>
  )
}

interface AppSidebarProps {
  collapsed?: boolean
  onToggleCollapsed?: () => void
}

export function AppSidebar({ collapsed = false, onToggleCollapsed }: AppSidebarProps) {
  const pathname = usePathname() ?? ''
  const { user } = useAuth()
  const [mobileOpen, setMobileOpen] = useState(false)

  return (
    <>
      <div className="lg:hidden sticky top-[64px] sm:top-[68px] z-30 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="flex items-center gap-1.5 px-3 h-12 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {mobileShortcuts.map((s) => {
            const active = isSidebarLinkActive(pathname, s.href)
            return (
              <Link
                key={s.href}
                href={s.href}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'flex items-center gap-1.5 shrink-0 rounded-full px-3 h-8 text-[13px] font-bold transition-colors',
                  active
                    ? 'bg-[rgb(var(--green-deep))] text-white'
                    : 'bg-secondary text-foreground/75 hover:text-foreground'
                )}
              >
                <s.icon className="w-3.5 h-3.5" aria-hidden="true" />
                {s.label}
              </Link>
            )
          })}
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="flex items-center gap-1.5 shrink-0 rounded-full px-3 h-8 text-[13px] font-bold bg-secondary text-foreground/75 hover:text-foreground transition-colors"
            aria-label="Abrir menu completo"
            aria-expanded={mobileOpen}
          >
            <Menu className="w-3.5 h-3.5" aria-hidden="true" />
            Menu
          </button>
        </div>
      </div>

      <aside
        className="hidden lg:flex flex-col sticky top-[64px] sm:top-[68px] h-[calc(100vh-64px)] sm:h-[calc(100vh-68px)] border-r border-border bg-card overflow-y-auto"
        aria-label="Menu lateral"
      >
        <div className="flex-1 overflow-y-auto">
          <SidebarNav pathname={pathname} collapsed={collapsed} user={user} />
        </div>
        {onToggleCollapsed && (
          <div className="border-t border-border p-2">
            <button
              type="button"
              onClick={onToggleCollapsed}
              className={cn(
                'flex items-center gap-2 w-full rounded-lg text-xs font-bold text-foreground/60 hover:text-foreground hover:bg-secondary transition-colors min-h-[36px]',
                collapsed ? 'justify-center px-0' : 'px-3'
              )}
              aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
              title={collapsed ? 'Expandir menu' : 'Recolher menu'}
            >
              {collapsed ? (
                <PanelLeftOpen className="w-4 h-4" aria-hidden="true" />
              ) : (
                <>
                  <PanelLeftClose className="w-4 h-4" aria-hidden="true" />
                  <span>Recolher</span>
                </>
              )}
            </button>
          </div>
        )}
      </aside>

      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 flex"
          role="dialog"
          aria-modal="true"
          aria-label="Menu da área logada"
        >
          <div
            className="absolute inset-0 bg-black/50 animate-fade-in"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <aside className="relative w-80 max-w-[90vw] h-full bg-card border-r-2 border-border overflow-y-auto animate-slide-in-right">
            <div className="flex items-center justify-between p-4 border-b border-border bg-card">
              <span className="text-sm font-bold text-foreground/80">Menu</span>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border hover:bg-secondary transition-colors"
                aria-label="Fechar menu"
              >
                <X className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>
            <SidebarNav pathname={pathname} user={user} onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}
    </>
  )
}
